import { useCallback, useEffect, useState } from 'react';
import { getSupabase } from '@/lib/supabase';
import { env } from '@/config/env';

export interface SecretRowStatus {
  key: string;
  target: 'netlify_env' | 'supabase_secret' | 'info';
  present: boolean;
}

export interface BootstrapStatus {
  netlifyToken: boolean;
  netlifySiteId: boolean;
  mgmtToken: boolean;
  projectRef: boolean;
}

export interface SecretsListResponse {
  bootstrap: BootstrapStatus;
  rows: SecretRowStatus[];
}

export interface SecretActionResult {
  ok: boolean;
  message?: string;
  stage?: 'test' | 'save';
}

const FN_URL = `${env.supabaseUrl}/functions/v1/secrets-manager`;

const authHeaders = async (): Promise<Record<string, string> | null> => {
  const supabase = getSupabase();
  if (!supabase) return null;
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return null;
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
};

export const useSecretsList = () => {
  const [data, setData] = useState<SecretsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const headers = await authHeaders();
    if (!headers) {
      setLoading(false);
      setError('Not authenticated.');
      return;
    }
    try {
      const res = await fetch(`${FN_URL}?op=list`, { headers });
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        setLoading(false);
        return;
      }
      const json = (await res.json()) as SecretsListResponse;
      setData(json);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'fetch failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
};

export const callSecret = async (op: 'test' | 'save', key: string, value: string): Promise<SecretActionResult> => {
  const headers = await authHeaders();
  if (!headers) return { ok: false, message: 'Not authenticated.' };
  try {
    const res = await fetch(FN_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({ op, key, value }),
    });
    const json = await res.json();
    if (!res.ok) return { ok: false, message: json.error ?? `HTTP ${res.status}` };
    return json as SecretActionResult;
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : 'request failed' };
  }
};
